import { Component, h, Prop, State, Watch, Element } from '@stencil/core';
import { getLinkTargetAttrs } from '../component-utils/ctaUtils';
import { showFoundationStyles, showRGCStyles } from '../component-utils/stencilUtils';
import axios from 'axios';

const AAFP_FOUNDATION_PRIMARY_COLOR = '#9E2A2F';
/**
 * COMPNAME is for creating a card with an Icon, Title and Text. Card is considered a CTA if linkTarget is set.
 * If a link is specified via linkTarget, the card's title will be linked. If no title, the link is ignored.
 *
 * <em>For More Information:</em>
 * AEM Component- ui.apps/src/main/content/jcr_root/apps/brand/aafp/components/content/aafp-icon-card
 * Sling Model- core/src/main/java/org/aafp/aem/core/models/AafpIconCardModel.java
 */
@Component({
  tag: 'aafp-icon-card',
  shadow: false,
})
export class AafpIconCard {
  /**
   * For creating the Alt Description
   */
  @Prop() iconCardAlt: string;
  /**
   * Path to the SVG file
   */
  @Prop() iconCardSrc: string;
  /**
   * For creating the main title
   */
  @Prop() iconCardTitle: string;
  /**
   * For creating the supporting text
   */
  @Prop() iconCardText: string;
  /**
   * The URL to wrap around the card
   */
  @Prop() linkTarget: string;
  /**
   * For setting the cta's click behavior
   */
  @Prop() linkIsExternal: boolean;

  @Element() element: HTMLElement;

  @State() svg: string | undefined;

  /**
   * If the image source changes and it
   * needs to be embedded, update the svg code
   */
  @Watch('iconCardSrc')
  watchPropHandler() {
    if (this.shouldEmbedSVG()) {
      this.getSvg().then((svg) => (this.svg = svg));
    }
  }

  componentWillLoad() {
    if (this.shouldEmbedSVG()) {
      this.getSvg().then((svg) => (this.svg = svg));
    }
  }

  /**
   * Fetch the markup of the specified SVG file
   * @return promise
   */
  async getSvg(): Promise<string> {
    const { data }: { data: string } = await axios.get(this.iconCardSrc);
    return data;
  }

  /**
   * Determine if the SVG should be
   * embedded in order to change its color
   * @return boolean
   */
  shouldEmbedSVG(): boolean {
    return (showFoundationStyles(document) || showRGCStyles(document)) && this.isSvgFile(this.iconCardSrc);
  }

  /**
   * Determine if the specified filename is an SVG
   * @param {string} filename
   * @return boolean
   */
  isSvgFile(filename: string) {
    if (filename) {
      return filename.split('.').pop() === 'svg';
    } else {
      return false;
    }
  }

  /**
   * Set the stroke color on an SVG
   * @param svgWrapper
   * @return void
   */
  setColor(svgWrapper: any) {
    const svgElement = svgWrapper.querySelector('svg');
    if (svgElement) {
      const g = svgElement.querySelector('g[stroke]:not([stroke="none"])');
      if (g) g.setAttribute('stroke', AAFP_FOUNDATION_PRIMARY_COLOR);
    }
  }

  /**
   * Render the icon. If it's part of a Foundation or RGC page,
   * AND an SVG, the file contents are fetched and a new stroke
   * color set.
   * @return string
   */
  renderIcon(): string | null {
    if (!this.iconCardSrc) {
      return null;
    }
    if (this.shouldEmbedSVG()) {
      return (
        <div
          class="adl-icon-card__icon-wrapper"
          innerHTML={this.svg}
          ref={(el: HTMLElement | undefined) => this.setColor(el)}
        />
      );
    }
    return (
      <div class="adl-icon-card__icon-wrapper">
        <img alt={this.iconCardAlt} src={this.iconCardSrc} class="adl-icon-card__icon" />
      </div>
    );
  }

  /**
   * Render the title. If a wrapping link is specified (linkTarget),
   * and the text has no links, wrap the title with it
   * @return string
   */
  renderTitle(): string | null {
    if (this.iconCardTitle && this.linkTarget) {
      return (
        <a
          href={this.linkTarget}
          class="adl-icon-card__anchor"
          {...getLinkTargetAttrs(this.linkIsExternal)}
          data-content={this.iconCardTitle}
        >
          <h3 class="adl-icon-card__title adl-h3" innerHTML={this.iconCardTitle} />
        </a>
      );
    } else if (this.iconCardTitle) {
      return <h3 class="adl-icon-card__title adl-h3" innerHTML={this.iconCardTitle} />;
    } else {
      return null;
    }
  }

  /**
   * Render the text. If there's a wrapping link, but no
   * title specified, and the text has no links, wrap the
   * link around this text.
   * @return string
   */
  renderText(): string | null {
    if (this.iconCardText) {
      return <div class="adl-icon-card__text" innerHTML={this.iconCardText} />;
    } else {
      return null;
    }
  }

  render() {
    return (
      <div class="adl-icon-card">
        <div class="adl-icon-card__wrapper">
          {this.renderIcon()}
          <div class="adl-icon-card__content">
            {this.renderTitle()}
            {this.renderText()}
          </div>
        </div>
      </div>
    );
  }
}
